import { useEffect, useState } from 'react';
import { MenuItem } from '@mui/material';
import {
  DialogContainer,
  DialogTitle,
  DialogBody,
  DialogActions,
  CustomAutoComplete,
  CustomTextField,
} from '../../../common/components';
import {
  INITIAL_BANK_ACCOUNT_STATE,
  BANK_NAME_OPTIONS,
  ACCOUNT_TYPE_OPTIONS,
} from '../constants/bankAccountsConstants';
import { validateBankAccount } from '../utils/bankAccountsUtils';
import { getCbuValidationMessage, isValidCbu, normalizeCbu } from '../../../utils/cbuUtils';

const BankAccountDialog = ({
  open,
  onClose,
  editMode,
  selectedBankAccount,
  onCreate,
  onUpdate,
  isSubmitting,
}) => {
  const [formData, setFormData] = useState(INITIAL_BANK_ACCOUNT_STATE);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      if (editMode && selectedBankAccount) {
        setFormData({
          uuid: selectedBankAccount.uuid,
          bank_name: selectedBankAccount.bank_name || '',
          account_number: selectedBankAccount.account_number || '',
          account_type: selectedBankAccount.account_type || '',
          cbu: selectedBankAccount.cbu || '',
          alias: selectedBankAccount.alias || '',
          is_active: selectedBankAccount.is_active ?? true,
        });
      } else {
        setFormData(INITIAL_BANK_ACCOUNT_STATE);
      }
      setErrors({});
    }
  }, [open, editMode, selectedBankAccount]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    const normalizedValue = name === 'cbu' ? normalizeCbu(value).slice(0, 22) : value;

    setFormData((prev) => ({
      ...prev,
      [name]: normalizedValue,
    }));

    if (errors[name]) {
      setErrors((prev) => {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }
  };

  const handleSubmit = async () => {
    const validationErrors = validateBankAccount(formData);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const payload = {
      bank_name: formData.bank_name,
      account_number: formData.account_number.trim(),
      account_type: formData.account_type,
      cbu: normalizeCbu(formData.cbu),
      alias: formData.alias ? formData.alias.trim() : null,
    };

    try {
      if (editMode && formData.uuid) {
        await onUpdate(formData.uuid, payload);
      } else {
        await onCreate(payload);
      }
      onClose();
    } catch (error) {
      console.error('Error al guardar la cuenta bancaria:', error);
    }
  };

  const normalizedCbu = normalizeCbu(formData.cbu);
  const cbuIsValid = normalizedCbu.length === 22 && isValidCbu(normalizedCbu);
  const cbuHelperText = errors.cbu || getCbuValidationMessage(normalizedCbu);

  return (
    <DialogContainer open={open} onClose={onClose} size="small">
      <DialogTitle>
        {editMode ? 'Editar cuenta bancaria' : 'Nueva cuenta bancaria'}
      </DialogTitle>

      <DialogBody sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <CustomAutoComplete
          label="Banco"
          name="bank_name"
          placeholder="Seleccioná un banco"
          value={formData.bank_name}
          onChange={handleChange}
          required
          error={Boolean(errors.bank_name)}
        >
          <MenuItem value="" disabled>
            Seleccioná un banco
          </MenuItem>
          {BANK_NAME_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </CustomAutoComplete>

        <CustomTextField
          label="Número de cuenta"
          name="account_number"
          value={formData.account_number}
          onChange={handleChange}
          required
          error={Boolean(errors.account_number)}
        />

        <CustomAutoComplete
          label="Tipo de cuenta"
          name="account_type"
          value={formData.account_type}
          onChange={handleChange}
          required
          error={Boolean(errors.account_type)}
        >
          <MenuItem value="" disabled>
            Seleccioná un tipo de cuenta
          </MenuItem>
          {ACCOUNT_TYPE_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </CustomAutoComplete>

        <CustomTextField
          label="CBU"
          name="cbu"
          value={formData.cbu}
          onChange={handleChange}
          required
          error={Boolean(errors.cbu)}
          helperText={cbuHelperText}
          FormHelperTextProps={{
            sx: cbuIsValid ? { color: 'success.main' } : undefined,
          }}
          inputProps={{ maxLength: 22 }}
        />

        <CustomTextField
          label="Alias"
          name="alias"
          value={formData.alias}
          onChange={handleChange}
          error={Boolean(errors.alias)}
        />
      </DialogBody>

      <DialogActions
        handleClose={onClose}
        handleSave={handleSubmit}
        saveText={editMode ? 'Actualizar' : 'Crear'}
        isSaving={isSubmitting}
      />
    </DialogContainer>
  );
};

export default BankAccountDialog;
